/**
 * Type guard to check if an object has a 'parentPage' property.
 *
 * This function helps in type narrowing by checking if the input object
 * has a 'parentPage' property, ensuring type safety when working with
 * nested page structures.
 *
 * @param {unknown} obj - The object to check for the 'parentPage' property.
 * @returns {boolean} True if the object has a 'parentPage' property, false otherwise.
 *
 * @typeParam T - The type of the object being checked.
 *
 * @example
 * // Returns true for an object with a parentPage
 * hasParentPageKey({ parentPage: { title: 'Parent' } })
 *
 * @example
 * // Returns false for an object without a parentPage
 * hasParentPageKey({ title: 'Page' })
 */
export const hasParentPageKey = (
  obj: unknown
): obj is { parentPage: unknown } => {
  return typeof obj === 'object' && obj !== null && 'parentPage' in obj
}

/**
 * Maps and normalizes page information, recursively handling parent pages.
 *
 * This function creates a standardized object representation of a page,
 * extracting and mapping key properties while handling nested parent pages.
 *
 * @param {any} obj - The input object to map.
 * @returns {Object} A mapped object with normalized page properties.
 *
 * @property {string} categoryTitle - The title of the category (defaults to empty string).
 * @property {boolean} subcategory - Whether the page is a subcategory (defaults to false).
 * @property {string} urlSlug - The URL slug for the page (defaults to empty string).
 * @property {string} categoryId - The unique identifier for the category (defaults to empty string).
 * @property {string} brand - The brand associated with the page (defaults to empty string).
 * @property {boolean} showMegalogo - Whether to show a mega logo (defaults to false).
 * @property {Object} [parentPage] - Recursively mapped parent page information.
 *
 * @example
 * // Maps a page object with parent page
 * mappedParentPage({
 *   categoryTitle: 'Clothing',
 *   urlSlug: 'mens-clothing',
 *   parentPage: { categoryTitle: 'Fashion' }
 * })
 *
 * @example
 * // Maps a page object without parent page
 * mappedParentPage({
 *   categoryTitle: 'Accessories',
 *   urlSlug: 'bags'
 * })
 */
export const mappedParentPage = (obj: any) => {
  if (!hasParentPageKey(obj)) obj

  const mappedObj: {
    categoryTitle?: string
    subcategory?: boolean
    urlSlug?: string
    categoryId?: string
    brand?: string
    showMegalogo?: boolean
    parentPage?: any
  } = {
    categoryTitle: obj.categoryTitle || '',
    subcategory: obj.subcategory || false,
    urlSlug: obj.urlSlug || '',
    categoryId: obj.categoryId || '',
    brand: obj.brand || '',
    showMegalogo: obj.showMegalogo || false,
  }

  if (hasParentPageKey(obj)) {
    mappedObj.parentPage = mappedParentPage(obj.parentPage)
  }

  return mappedObj
}
